/**
 * The camera that fills the viewport with one sphere, for framing a single group.
 *
 * A sibling of `fitNetworkZoom` rather than a call to it, and the difference is that this one does
 * not have to measure anything. `fitNetworkZoom` projects a point cloud because it cannot know its
 * shape; a sphere centred on the camera's own target sits at depth zero, where the foreshortening
 * divisor is exactly one - so its projected radius is its world radius times `pixelsPerUnit`, and
 * the zoom that frames it is arithmetic rather than a measurement.
 *
 * The orientation is left alone. Framing a group answers "show me this", not "show me this from
 * somewhere else", and a reader who has turned the estate to a particular angle has usually turned
 * it to see something - taking that away as the price of a closer look is the kind of help nobody
 * asked for.
 */

import type { FitInset } from '../../geometry/fitZoom';
import type { Size, ViewCamera } from '../../types';
import type { Vec3 } from '../types';

export type FrameNetworkSphereArgs = {
  camera: ViewCamera;
  /** In CSS pixels, since the inset is in CSS pixels. */
  viewport: Size;
  center: Vec3;
  radius: number;
  inset: FitInset;
};

/**
 * How much of the viewport a framed group is allowed to fill.
 *
 * Short of all of it. A group framed edge to edge has nothing around it, and the thing a reader
 * most wants to know on arriving is what the group is *attached* to - the references leaving it
 * and the marks at their other ends. Four fifths keeps a ring of that context on screen.
 */
const SURROUNDINGS = 0.8;

export const frameNetworkSphere = ({
  camera,
  viewport,
  center,
  radius,
  inset,
}: FrameNetworkSphereArgs): ViewCamera => {
  const availableX = viewport.width / 2 - inset.horizontal;
  const availableY = viewport.height / 2 - inset.vertical;
  const span = Math.min(viewport.width, viewport.height) / 2;

  /* A degenerate sphere, an unmeasured viewport, or a pane too small for its own inset: all three
     leave nothing to frame, and the camera the reader already has is a better answer than one
     divided by zero. */
  if (radius <= 0 || span <= 0 || availableX <= 0 || availableY <= 0) {
    return { ...camera, target: center };
  }

  /**
   * `heightScale` is 1 for every network view, so a sphere stays a sphere.
   *
   * Were it not, the z axis would be stretched and this would be framing an ellipsoid by its
   * equator - too tight along z. Left as a note rather than handled, because the camera that
   * would need it is the one the billboards cannot survive anyway: `toUnitCube` documents why the
   * network never stretches z.
   */
  const projectedRadius = radius * span;

  return {
    ...camera,
    target: center,
    zoom: (Math.min(availableX, availableY) * SURROUNDINGS) / projectedRadius,
  };
};
